import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowLeft, FiTrash2, FiUser, FiSearch, FiLoader, FiMessageCircle } from 'react-icons/fi'
import ContactChatPanel from '../../components/admin/ContactChatPanel'

export default function WebsiteCustomers() {
  const { websiteId } = useParams()
  const navigate = useNavigate()

  // Section toggle state
  const [activeSection, setActiveSection] = useState('customers') // 'customers' or 'conversations'

  const [summary, setSummary] = useState(null)
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [activity, setActivity] = useState([])
  const [activityPage, setActivityPage] = useState(1)
  const [activityLoading, setActivityLoading] = useState(false)

  useEffect(() => {
    fetchSummary()
    fetchCustomers()
  }, [websiteId, page, search, statusFilter])

  useEffect(() => {
    if (selected) fetchActivity(selected.customer._id)
  }, [selected, activityPage])

  const setAuthHeader = () => {
    const token = localStorage.getItem('userAdminToken') || localStorage.getItem('token') || localStorage.getItem('globalAdminToken')
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  const fetchSummary = async () => {
    try {
      setAuthHeader()
      const res = await axios.get(`/customer-auth/admin/${websiteId}/customers/summary`)
      setSummary(res.data.summary)
    } catch (err) {
      console.error('Error fetching summary', err)
    }
  }

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setAuthHeader()
      const res = await axios.get(`/customer-auth/admin/${websiteId}/customers`, { params: { page, limit, search, status: statusFilter } })
      setCustomers(res.data.customers || [])
      const pagination = res.data.pagination || {}
      setTotalPages(pagination.pages || 1)
    } catch (err) {
      console.error('Error fetching customers', err)
      toast.error(err.response?.data?.message || 'Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  const fetchActivity = async (custId) => {
    try {
      setActivityLoading(true)
      setAuthHeader()
      const res = await axios.get(`/customer-auth/admin/${websiteId}/customers/${custId}/activity`, { params: { page: activityPage, limit: 10 } })
      setActivity(res.data.activities || [])
    } catch (err) {
      console.error('Error fetching activity', err)
      toast.error('Failed to load activity')
    } finally {
      setActivityLoading(false)
    }
  }

  const selectCustomer = (c) => {
    setSelected(c)
    setActivityPage(1)
    setActivity([])
  }

  const updateCustomerStatus = async (cust, nextStatus) => {
    const confirmText = nextStatus === 'suspended' ? 'Suspend this customer?' : 'Reactivate this customer?'
    if (!window.confirm(confirmText)) return

    try {
      setAuthHeader()
      await axios.put(`/customer-auth/admin/${websiteId}/customers/${cust.customer._id}/status`, { status: nextStatus })
      toast.success(nextStatus === 'suspended' ? 'Customer suspended' : 'Customer reactivated')
      fetchCustomers()
      if (selected && selected.customer._id === cust.customer._id) setSelected(null)
    } catch (err) {
      console.error('Error updating customer status', err)
      toast.error('Failed to update customer status')
    }
  }

  const deleteCustomer = async (cust) => {
    if (!window.confirm('Delete this customer permanently? This will remove the account from the database.')) return
    try {
      setAuthHeader()
      await axios.delete(`/customer-auth/admin/${websiteId}/customers/${cust.customer._id}`)
      toast.success('Customer deleted')
      fetchCustomers()
      if (selected && selected.customer._id === cust.customer._id) setSelected(null)
    } catch (err) {
      console.error('Error deleting customer', err)
      toast.error('Failed to delete customer')
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><FiLoader className="w-12 h-12 animate-spin text-primary-600" /></div>

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/user-admin/websites')} className="p-2 hover:bg-gray-100 rounded" title="Back to Website Builder Dashboard"> <FiArrowLeft /> </button>
          <h1 className="text-2xl font-bold">Customer Management</h1>
        </div>

        {/* Section Navigation Tabs */}
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-0">
            <button
              onClick={() => setActiveSection('customers')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition ${
                activeSection === 'customers'
                  ? 'text-blue-600 border-b-blue-600'
                  : 'text-gray-600 border-b-transparent hover:text-gray-900'
              }`}
            >
              Customers
            </button>
            <button
              onClick={() => setActiveSection('conversations')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition flex items-center gap-2 ${
                activeSection === 'conversations'
                  ? 'text-blue-600 border-b-blue-600'
                  : 'text-gray-600 border-b-transparent hover:text-gray-900'
              }`}
            >
              <FiMessageCircle className="w-4 h-4" />
              All Conversations
            </button>
          </div>
        </div>
      </div>

      {/* Customers Section */}
      {activeSection === 'customers' && (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4 h-fit">
          <h2 className="font-semibold mb-4 text-base sm:text-lg">Summary</h2>
          {summary ? (
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span>Total Customers</span><strong>{summary.totalCustomers}</strong></div>
              <div className="flex justify-between"><span>Active</span><strong>{summary.activeCustomers}</strong></div>
              <div className="flex justify-between"><span>New (last 30d)</span><strong>{summary.newCustomers}</strong></div>
              <div className="flex justify-between"><span>Suspended</span><strong>{summary.suspendedCustomers}</strong></div>
              <div className="flex justify-between"><span>Last Login</span><strong>{summary.lastLogin ? new Date(summary.lastLogin).toLocaleString() : '—'}</strong></div>
              <div className="flex justify-between"><span>Orders</span><strong>{summary.numberOfOrders}</strong></div>
              <div className="flex justify-between"><span>Reviews</span><strong>{summary.numberOfReviews}</strong></div>
              <div className="flex justify-between"><span>Payments</span><strong>{summary.numberOfPayments}</strong></div>
              <div className="flex justify-between"><span>Appointments</span><strong>{summary.numberOfAppointments}</strong></div>
            </div>
          ) : (
            <div className="text-gray-500 text-sm">No summary available</div>
          )}

          <div className="mt-6">
            <h3 className="font-semibold mb-2">Search</h3>
            <div className="flex gap-2">
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} className="flex-1 p-2 border rounded" placeholder="Name, email or phone" />
              <button onClick={() => fetchCustomers()} className="px-3 py-2 bg-primary-600 text-white rounded"><FiSearch /></button>
            </div>
            <div className="mt-3">
              <label className="text-xs">Status</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }} className="w-full p-2 border rounded mt-1">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold">Customer List</h2>
            <div className="text-sm text-gray-500">Page {page} / {totalPages}</div>
          </div>

          {customers.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No customers found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="p-2">Name</th>
                    <th className="p-2">Email</th>
                    <th className="p-2">Phone</th>
                    <th className="p-2">Registered</th>
                    <th className="p-2">Last Login</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Orders</th>
                    <th className="p-2">Reviews</th>
                    <th className="p-2">Payments</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map(c => (
                    <tr key={c.customer._id} className="border-t">
                      <td className="p-2">{c.customer.firstName} {c.customer.lastName}</td>
                      <td className="p-2">{c.customer.email}</td>
                      <td className="p-2">{c.customer.phone}</td>
                      <td className="p-2">{new Date(c.customer.createdAt).toLocaleDateString()}</td>
                      <td className="p-2">{c.customer.lastLoginAt ? new Date(c.customer.lastLoginAt).toLocaleString() : '—'}</td>
                      <td className="p-2">{c.customer.status}</td>
                      <td className="p-2">{c.counts.totalOrders}</td>
                      <td className="p-2">{c.counts.reviews}</td>
                      <td className="p-2">{c.counts.payments}</td>
                      <td className="p-2">
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => selectCustomer(c)} className="py-1 px-2 bg-blue-600 text-white rounded text-xs flex items-center gap-2"><FiUser /> View</button>
                          <button onClick={() => updateCustomerStatus(c, c.customer.status === 'suspended' ? 'active' : 'suspended')} className="py-1 px-2 bg-amber-500 text-white rounded text-xs">
                            {c.customer.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                          </button>
                          <button onClick={() => deleteCustomer(c)} className="py-1 px-2 bg-red-500 text-white rounded text-xs flex items-center gap-2"><FiTrash2 /> Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div>
              <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-2 bg-gray-100 rounded">Previous</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="ml-2 px-3 py-2 bg-gray-100 rounded">Next</button>
            </div>
            <div className="text-xs text-gray-500">Showing {customers.length} customers</div>
          </div>

          <div className="mt-6">
            {!selected ? (
              <div className="text-gray-500 text-center py-8">Select a customer to view profile and activity</div>
            ) : (
              <div>
                <h3 className="text-lg font-bold">{selected.customer.firstName} {selected.customer.lastName}</h3>
                <div className="text-sm text-gray-500">{selected.customer.email} • {selected.customer.phone}</div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 p-3 rounded">Registered: <div className="font-semibold">{new Date(selected.customer.createdAt).toLocaleString()}</div></div>
                  <div className="bg-gray-50 p-3 rounded">Email Verified: <div className="font-semibold">{selected.customer.isEmailVerified ? 'Yes' : 'No'}</div></div>
                  <div className="bg-gray-50 p-3 rounded">Last Login: <div className="font-semibold">{selected.customer.lastLoginAt ? new Date(selected.customer.lastLoginAt).toLocaleString() : '—'}</div></div>
                  <div className="bg-gray-50 p-3 rounded">Status: <div className="font-semibold">{selected.customer.status}</div></div>
                </div>

                <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <FiMessageCircle className="text-blue-600" />
                    <div>
                      <h4 className="font-semibold">Contact Us Message Chat</h4>
                      <p className="text-sm text-gray-500">Manage conversations for this website’s customers from one place.</p>
                    </div>
                  </div>
                  <ContactChatPanel websiteId={websiteId} selectedCustomer={selected} />
                </div>

                <div className="mt-4">
                  <h4 className="font-semibold">Activity</h4>
                  {activityLoading ? (
                    <div className="py-6 text-center"><FiLoader className="w-8 h-8 animate-spin mx-auto text-primary-600" /></div>
                  ) : activity.length === 0 ? (
                    <div className="text-gray-500 py-4">No activity found</div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto mt-2">
                      {activity.map(a => (
                        <div key={a._id} className="p-2 border rounded bg-white">
                          <div className="text-sm font-semibold">{a.activityType}</div>
                          <div className="text-xs text-gray-600">{a.description}</div>
                          <div className="text-xs text-gray-400">{new Date(a.createdAt).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* All Conversations Section */}
      {activeSection === 'conversations' && (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FiMessageCircle className="text-blue-600" />
              Contact Us Message Chat
            </h2>
            <p className="text-sm text-gray-500 mt-1">Browse and manage all customer Contact Us conversations for this website.</p>
          </div>
          <ContactChatPanel websiteId={websiteId} />
        </div>
      </div>
      )}
    </div>
  )
}
